import React, { useState, useEffect, useCallback } from 'react';
import { List, Snackbar, Icon, Button } from '@material-ui/core';
import { GridContainer, CenterContent } from 'global/components/perform-grid';
import { useLocation } from 'react-router-dom';
import NavToTop from 'feature/nav/NavToTop';
import useStyles from './styles';
import { get, set, size, debounce, upperFirst } from 'global/lib/lodash';
import SideBar from './sidebar';
import Filters from './filters';
import TaskNotify from '../../task/TaskNotify';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { ScoreLoading } from 'global/components/skeleton-loader';
import { Alert } from 'global/components/alerts';
import TaskBanner from './TaskBanner';
import sendData from 'redux/actions/sendData';
import { useDispatch } from 'react-redux';
import { addNewTask } from 'redux/actions/view';
import { wait } from 'global/utils';
import { useAccessEdition } from 'global/utils/edition';
import { awaitAll } from 'global/utils/awaitAll';
import useDebounce from 'global/hooks/useDebounce';
import { useUser } from 'global/hooks/useUser';
import { getItemType, getItemKey, getId, isGob, filterData } from './utils';
import { ActiveEdit } from './ActiveEdit';
import { SystemScore } from './SystemScore';
import { useScreen } from 'global/styles';
import { ObservationCreateFab } from './ObservationCreate';
import { stringObj } from '../../../global/utils';
export * from './utils';

const specialchars = ['_', '*', '/', `\\`, '[', `]`, `{`, `}`, `~`, '=', '+'];

const ReportAssessmentScoreContent = (props) => {
  const { assessment, scores, printView, scIndex, canShowScores, isCourse, onToggleEdit, isTemplate } = props;

  //state
  const [filterLoading, setFilterLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState({});
  const [showSelectBoxes, setShowSelectBoxes] = useState(false);
  const [scorecardIndex, setScorecardIndex] = useState(scIndex !== undefined ? scIndex : 0);
  const [origStatus, setOrigStatus] = useState(get(assessment, 'data.status', 'draft'));
  let [allData, setAllData] = useState(get(scores, 'data', []));
  let [treed, setTreed] = useState([]);
  const [sidebarOpen, setSidebar] = useState(false);
  const [addSubscoreOpen, setAddSubscoreOpen] = useState(false);
  const [activeEdit, _setActiveEdit] = useState();
  const [alerts, setAlerts] = useState();
  const [searchString, setSearchString] = useState('');
  const [filter, setFilter] = useState({
    value: 'any',
    hideNa: !(isCourse || ['draft', 'template'].includes(get(assessment, 'data.status', 'draft'))),
    search: '',
    listGroup: isCourse || isTemplate ? 'category' : get(assessment, 'data.scorecards.0.config.defaultDisplay') === 'realm' ? 'location' : 'category',
    onlyLoc: false,
  });
  const [filterBtns, setFilterBtns] = useState([]);

  //data
  const scorecards = get(assessment, 'data.scorecards', []);
  const activeSc = get(scorecards, scorecardIndex, {});
  const scid = get(activeSc, 'id');
  const scoredata = get(allData, scid, []);
  const status = get(assessment, 'data.status', 'draft');
  const assessment_id = get(assessment, 'data.id');
  const realm = get(assessment, 'data.realm');
  const realm_id = get(realm, 'id');
  const userCanEdit = (() => {
    if (status === 'published') {
      return false;
    }

    if (props.userCanEdit) {
      return true;
    } else {
      return false;
    }
  })();
  const hasSubScores = !isTemplate && !isCourse && get(activeSc, `config.hasSubScores`, false);

  const realmFilter = (() => {
    if (!realm || !hasSubScores) {
      return null;
    }

    const options = {
      division: 'community',
      community: 'lot',
      lot: 'room',
    };
    return {
      tier: get(options, realm.tier),
      parent_id: realm.tier == 'lot' ? undefined : realm.id,
    };
  })();

  const rooms = useRetrieveData(
    { url: '/realms' },
    {
      useEffect: true,
      waitFor: realmFilter,
      build: ['realms'],
      params: {
        filter: realmFilter || {},
      },
    }
  );

  const assessmentLoading = get(assessment, 'loading');
  const loading = assessmentLoading || get(scores, 'loading');
  const error = get(scores, 'error', false);

  //hooks
  const { isMobile } = useScreen();
  const location = useLocation();
  const { me } = useUser();
  const dispatch = useDispatch();
  const userCanLink = useAccessEdition(get(activeSc, `editionId`)) && get(filter, 'listGroup') !== 'location';
  const classes = useStyles({ ...props, isMobile });
  const _searchStringDebounced = useDebounce(searchString, 700);
  const searchStringDebounced = size(_searchStringDebounced) >= 3 || specialchars.find((f) => _searchStringDebounced.indexOf(f) > -1) ? _searchStringDebounced : null;
  const assessmentTasks = useRetrieveData(
    { url: '/tasks' },
    {
      waitFor: assessment_id && realm_id ? undefined : null,
      build: ['tasks'],
      params: {
        filter: {
          assessment: assessment_id,
          realm: realm_id,
        },
        include: 'subScore',
      },
    }
  );
  const setCurrentAnchor = useCallback(
    debounce(
      (toShow, goTo = false) => {
        if (props.isReview) {
          return;
        }
        location.state = { id: getId(toShow) };
      },
      50,
      { leading: false }
    )
  );

  //effects
  useEffect(() => {
    setAllData(get(scores, 'data', []));
  }, [scores.data]);

  useEffect(() => {
    // this triggers when opening to the scores tab before opening other parts of assessments
    // (because it will assume the assessment is in "Draft" state until it's loaded)
    // and also triggers when you change assessment info while scoring (status)
    buildFilters();
    if (!assessmentLoading && status !== origStatus) {
      update();
      setOrigStatus(status);
    }
  }, [assessmentLoading, status, scorecardIndex]);

  useEffect(() => {
    if (!loading) {
      runFilter();
    }
  }, [filter, searchStringDebounced, loading]);

  useEffect(() => {
    if (error) {
      setAlerts({
        message: `Error: ${error.message}`,
        severity: 'error',
      });
    }
  }, [error]);

  useEffect(() => {
    if (onToggleEdit) {
      onToggleEdit(activeEdit);
    }
  }, [activeEdit, onToggleEdit]);

  useEffect(() => {
    // If the user changes filters while in Edit Mode,
    // we're going to change their current active checklist
    // to a checklist that has filtered content
    if (activeEdit) {
      const agg = get(treed, '0.children', []).reduce((agg, item) => {
        get(item, 'children', []).forEach((cl) => {
          if (size(get(cl, 'children')) > 0) {
            if (getId(cl) === getId(get(activeEdit, 'checklist'))) {
              agg = false;
            } else if (agg === true) {
              agg = { checklist: cl, system: item };
            }
          }
        });
        return agg;
      }, true);

      if (get(agg, 'checklist')) {
        setActiveEdit(agg);
      }
    }
  }, [treed]);

  useEffect(() => {
    const startingpage = props.page;
    return () => {
      //scores doesn't get updated when switching tabs because we update the treed data manually.
      // so need to get latest data when we leave the page. but only if we are still in this assessment
      if (window.location.pathname.includes(startingpage) && scores) {
        scores.update();
      }
    };
  }, []);

  //actions
  const buildFilters = () => {
    const defaultDisplay = hasSubScores ? get(activeSc, 'config.defaultDisplay', 'system') : 'system';

    setFilter({
      value: 'any',
      listGroup: defaultDisplay === 'realm' ? 'location' : 'category',
      onlyLoc: false,
      hideNa: !(isCourse || ['draft', 'template'].includes(status)),
      search: '',
    });

    let options = [];

    if (get(activeSc, 'config.allowNA')) {
      options.push('N/A');
    }
    if (get(activeSc, 'config.allowNO')) {
      options.push('N/O');
    }
    if (get(activeSc, 'config.maxScore') === 1) {
      options.push('PASS');
      options.push('FAIL');
    } else {
      const min = get(activeSc, 'config.minScore', '0');
      const max = get(activeSc, 'config.maxScore', '4');
      const step = get(activeSc, 'config.step', '1');
      [...Array(max + 1).keys()].forEach((y) => {
        if (y >= min && (y + min) % step === 0) {
          options.push(y.toString());
        }
      });
    }

    options = options.map((o) => ({ text: o, val: o }));

    setFilterBtns(
      hasSubScores
        ? [
            {
              val: 'listGroup',
              type: 'select',
              options: [
                { text: 'By Category', val: 'category' },
                { text: 'By Location', val: 'location' },
              ],
            },
            { text: 'Any', val: 'any' },
            { val: 'value', type: 'select', options },
            {
              text: 'Subscores',
              val: 'filter.onlyLoc',
              type: 'switch',
              disabled: (p) => get(p, 'selected.listGroup') === 'location',
              onChange: (val) => setFilter((f) => ({ ...f, value: 'any', onlyLoc: val })),
            },
            {
              text: 'Hide N/A',
              val: 'filter.hideNa',
              type: 'switch',
              disabled: (p) => get(p, 'selected.listGroup') === 'location',
              onChange: (val) => setFilter((f) => ({ ...f, value: 'any', hideNa: val })),
            },
          ]
        : [
            { text: 'Any', val: 'any' },
            { text: 'High', val: 'high' },
            { text: 'Low', val: 'low' },
            { val: 'value', type: 'select', options },
            { text: 'Hide N/A', val: 'filter.hideNa', type: 'switch', onChange: (val) => setFilter((f) => ({ value: 'any', hideNa: val })) },
          ]
    );
  };

  const update = async () => {
    if (get(scores, 'update')) {
      await scores.update();
    }
  };

  const setActiveEdit = (p) => {
    if (!get(p, 'checklist')) {
      return _setActiveEdit();
    }
    if (get(p, 'scroll')) {
      document.getElementById('allscores').scrollIntoView(true);
      window.scrollBy(0, get(p, 'scrollBy', 48));
    }
    _setActiveEdit({
      system: p.system,
      checklist: p.checklist,
    });
  };

  const runFilter = async (d = scoredata) => {
    setFilterLoading(true);
    const { hasChildren } = await filterData(d, { ...filter, search: searchStringDebounced, activeEdit: activeEdit, assessment: assessment });
    setFilterLoading(false);
    setTreed(hasChildren);
    _setActiveEdit();
  };

  const setSidebarOpen = (val) => {
    document.getElementsByTagName('body')[0].removeAttribute('style');
    setSidebar(val);
  };

  const updateScores = async (d, needsFullUpdate) => {
    if (needsFullUpdate) {
      if (!loading) {
        // setAlerts({
        //     message: `Error: Failed to update correctly. Updating tree to ensure we are up to date. Please wait.`,
        //     severity: 'error',
        // });
        await wait(2000);
        await update();
      }
      return;
    } else {
      const item = get(d, '0');
      const checkpoint = get(item, 'processedScorecardScoreValue');
      const checklist = get(checkpoint, 'parent');
      const system = get(checklist, 'parent');
      const isGobs = isGob(checkpoint);

      assessment.update({ loading: false });

      let replaced = false;

      let newAll = scoredata.map((dd) => {
        if (getId(dd) == getId(checklist)) {
          console._log('found checklist', checklist, dd);
          dd = {
            ...dd,
            ...checklist,
            children: undefined,
            parent: undefined,
            id: dd.id,
            parentId: dd.parentId,
          };
          replaced = true;
        } else if (getId(dd) == getId(system)) {
          console._log('found system', system, dd);
          dd = {
            ...dd,
            ...system,
            id: dd.id,
            parentId: dd.parentId,
          };
          replaced = true;
        } else {
          get(checklist, 'children', []).map((cchild) => {
            if (getId(dd) == getId(cchild)) {
              dd = {
                ...dd,
                ...cchild,
                parent: undefined,
                id: dd.id,
                parentId: dd.parentId,
              };
              replaced = true;
            }
          });
        }
        return dd;
      });

      if (isGobs) {
        const found = newAll.find((na) => getId(na) === getId(checkpoint));
        if (!found) {
          newAll = [{ ...checkpoint, parent: undefined }, ...newAll];
        }
        replaced = true;
      }

      if (!replaced) {
        return await update();
      }

      const { hasChildren } = await filterData(newAll, { ...filter, search: searchStringDebounced, activeEdit: activeEdit, assessment: assessment }); //, scorecardId: get(scorecards,`${scorecardIndex}.id`)
      //need to do this because the state isn't necessarily getting updated before this method is called again, so the alldata is stale by that point
      treed = hasChildren;
      setTreed(hasChildren);
      allData[activeSc.id] = newAll;
      setAllData({ ...allData });
    }
  };

  const isLocationView = get(filter, 'listGroup') === 'location';
  const isCategoryView = get(filter, 'listGroup') === 'category';

  const itemPasses = (item) => {
    if (item.value === null) {
      return false;
    }

    if (isLocationView) {
      return true;
    } else if (isCategoryView && item.isSubScore) {
      return false;
    }

    const value = parseInt(item.value);

    if (value === 100 || value === -1) {
      const hasImage = size(item.images) > 0;
      const hasNotes = size(item.notes) > 0;
      const isGobs = isGob(item);
      const hasSubs = size(item.subScores) > 0;

      if (!hasImage && !hasNotes && !isGobs && !hasSubs) {
        return false;
      }
    }

    return true;
  };

  const createKey = (item) => {
    const itemType = getItemType(item);
    return {
      ...item,
      itemType,
    };
  };

  const findParent = (it) => {
    const searchChildren = (children, id) => {
      const childs = children || [];
      let found = childs.find((c) => c.id == id);

      if (found) {
        return found;
      } else {
        for (let i = 0; i < childs.length; i++) {
          found = searchChildren(childs[i].children, id);
          if (found) {
            break;
          }
        }
      }
      return found;
    };

    return searchChildren(treed, it.parentId);
  };

  const selectItem = (item) => {
    //#region check first
    if (item.value == null && !isLocationView) {
      return setAlerts({
        message: `Error: Not allowed to select an unscored item.`,
        severity: 'error',
      });
    }

    const passes = itemPasses(item);
    if (!passes) {
      return setAlerts({
        message: `Error: High scores and N/O scores must have notes or images attached in order to select.`,
        severity: 'error',
      });
    }
    //#endregion
    setShowSelectBoxes(true);

    const itemType = getItemType(item);
    let adds = 0;

    let selItems = { ...selectedItems };
    const exists = get(selItems, getItemKey(item)) || item.selectAll === false;

    const setAndAdd = (it) => {
      set(selItems, getItemKey(it), createKey(it));
      const iType = getItemType(it);

      if (iType === 'checkpoint') {
        adds += 1;

        const parent = findParent(it);

        const maxPass = [];
        const currentCount = [];
        (get(parent, 'children') || []).map((child) => {
          if (itemPasses(child)) {
            maxPass.push(child);
          }
          if (get(selItems, getItemKey(child))) {
            currentCount.push(child);
          }
        });
        setAndAdd({ ...parent, fullCount: size(maxPass) === size(currentCount) });

        //calc parentparent, ie system
        const parentParent = parent && findParent(parent);
        if (parentParent) {
          const allPass = [];
          parentParent.children.map((pchild) => {
            const maxPass = [];
            const currentCount = [];
            (pchild || []).children.map((child) => {
              if (itemPasses(child)) {
                maxPass.push(child);
              }
              if (get(selItems, getItemKey(child))) {
                currentCount.push(child);
              }
            });
            allPass.push(size(maxPass) === size(currentCount) ? true : false);
          });
          setAndAdd({ ...parentParent, fullCount: size(allPass.filter((f) => f)) === size(allPass) });
        }
      }
    };

    const removeItem = (it) => {
      const iType = getItemType(it);
      delete selItems[getItemKey(it)];

      if (iType === 'checkpoint') {
        const checklistHasOtherCheckpoints = Object.keys(selItems).filter((k) => get(selItems, [k, 'parentId']) == it.parentId);
        if (size(checklistHasOtherCheckpoints) === 0) {
          removeItem(findParent(it));
        } else {
          const parent = findParent(it);
          const maxPass = [];
          const currentCount = [];
          (get(parent, 'children') || []).map((child) => {
            if (itemPasses(child)) {
              maxPass.push(child);
            }
            if (get(selItems, getItemKey(child))) {
              currentCount.push(child);
            }
          });

          const parentParent = parent && findParent(parent);
          if (parentParent) {
            //should be system
            const parentOthers = Object.keys(selItems).filter((k) => get(selItems, [k, 'parentId']) == parent.parentId);
            if (size(parentOthers)) {
              set(selItems, getItemKey(parentParent), { ...parentParent, fullCount: false });
            } else {
              removeItem(parentParent);
            }
          }

          setAndAdd({ ...parent, fullCount: size(maxPass) === size(currentCount) });
        }
      } else if (iType === 'checklist') {
        const systemHasOtherChecklists = Object.keys(selItems).filter((k) => get(selItems, [k, 'parentId']) == it.parentId);
        if (size(systemHasOtherChecklists) === 0) {
          removeItem(findParent(it));
        }
      }
    };

    if (exists) {
      removeItem(item);

      if (itemType === 'checklist' || itemType === 'room') {
        (item.children || []).map((child) => {
          removeItem(child);
        });
      } else if (itemType === 'superset' || itemType === 'system') {
        (item.children || []).map((child) => {
          (child.children || []).map((cchild) => {
            removeItem(cchild);
          });
          removeItem(child);
        });

        if (itemType === 'system') {
          removeItem(item);
        }
      } else if (itemType === 'selectAll') {
        selItems = {};
      }
    } else {
      setAndAdd(item);

      if (itemType === 'checklist' || itemType === 'room') {
        (item.children || []).map((child) => {
          if (itemPasses(child)) {
            setAndAdd(child);
          }
        });
      } else if (itemType === 'superset' || itemType === 'system') {
        (item.children || []).map((child) => {
          (child.children || []).map((cchild) => {
            if (itemPasses(cchild)) {
              setAndAdd(cchild);
            }
          });
        });
      } else if (itemType === 'selectAll') {
        get(treed, '0.children', []).map((supe) => {
          (supe.children || []).map((child) => {
            (child.children || []).map((cchild) => {
              if (itemPasses(cchild)) {
                setAndAdd(cchild);
              }
            });
          });
        });
      }
    }

    if (adds > 0) {
      setAlerts({
        message: `You added ${adds} new item${adds > 1 ? 's' : ''}.`,
      });
    }

    setSelectedItems(selItems);
  };

  const tv = get(treed, '0.children', []);

  const hasScore = (() => {
    const adata = get(assessment, 'data');
    const scores = get(adata, 'scores');
    const scardId = get(adata, ['scorecards', scorecardIndex, 'id']);
    return get(scores, [scardId, 'calculatedScore']);
  })();

  const createTasks = async (formState) => {
    const checkpoints = Object.keys(selectedItems)
      .filter((k) => get(selectedItems, [k, 'itemType']) === 'checkpoint')
      .map((cp) => get(selectedItems, cp));

    const order = []; //put the checkpoints in order so that the PDF renders them correctly
    const pushOrder = (check) => {
      if (size(check.subscores)) {
        check.subscores.map((ss) => order.push(ss));
      } else {
        order.push(check);
      }
    };
    (tv || []).map((sup) => {
      const childs = get(sup, 'children', []);
      childs.map((child) => {
        const check = checkpoints.find((cp) => getId(cp) == getId(child));
        if (check) {
          pushOrder(check);
        } else if (child.children) {
          child.children.map((_child) => {
            const _check = checkpoints.find((cp) => getId(cp) == getId(_child));
            if (_check) {
              pushOrder(_check);
            }
          });
        }
      });
    });

    await awaitAll(
      order.map((checkpoint) => () => async () => {
        const { isSubScore } = checkpoint;
        const checklist = findParent(checkpoint) || checkpoint.checklist;

        const isGobs = isGob(checkpoint);

        const createdTask = await sendData(
          {
            url: `/tasks?include=images,assignees,viewers,authors,comments,comments.author,comments.images,realm,scorecardScore,scorecardScore.checklist,scorecardScore.checkpoint,scorecardScore.checkpoint.checkpointOptions`,
            method: 'post',
            data: {
              data: {
                type: 'tasks',
                attributes: stringObj({
                  name: isGobs ? `${get(checklist, 'name', '')}: General Observations` : `${get(checklist, 'name', '')}: ${get(checkpoint, 'name')}`,
                  description: isGobs ? get(checkpoint, 'criteria') : get(checkpoint, 'notes'),
                  status: 0,
                  realm_id,
                  assessment_id,
                  scorecard_score_id: !isSubScore ? get(checkpoint, 'scorecardScoreId', '') : undefined,
                  sub_score_id: isSubScore ? checkpoint.id : undefined,
                  checkpoint_id: isGobs ? null : get(checkpoint, 'checkpointId', ''),
                }),
                relationships: {
                  authors: {
                    data: [stringObj({ type: 'users', role: 'author', id: me.id })],
                  },
                  assignees: {
                    data: get(formState, 'assignedTo', []).map((a) => stringObj({ type: 'users', role: 'assignee', id: a.id })),
                  },
                },
              },
            },
          },
          { normalize: { build: ['tasks'] } }
        );

        const task = get(createdTask, '0');
        dispatch(addNewTask(task));
        return task;
      }),
      { queueSize: 1 }
    );

    setSelectedItems({});
    assessmentTasks.update();
  };

  if (window.SUPPORT) {
    window.UPDATE = update;
  }

  if (!canShowScores) {
    if (assessmentLoading) {
      return (
        <GridContainer>
          <CenterContent fullWidth={printView} selfCenter>
            <div style={{ padding: 12 }}>
              <ScoreLoading />
            </div>
          </CenterContent>
        </GridContainer>
      );
    }
    return (
      <GridContainer>
        <CenterContent fullWidth={printView} selfCenter>
          <div style={{ padding: 12 }}>{`You don't have access to scores while this report is in ${upperFirst(status)}.`}</div>
        </CenterContent>
      </GridContainer>
    );
  }

  const subscoreProps = {
    scorecard: activeSc,
    rooms: {
      ...rooms,
      update: (e) => {
        rooms.update(e);
        get(window, 'MASTERREALM.update', () => 0)();
      },
      realmFilter,
    },
    allScoreData: scoredata,
    updateScores,
    assessment,
    setAlerts,
    activeEdit,
    hasSubScores,
  };

  return (
    <>
      <NavToTop visible={!loading && !activeEdit && !!size(tv)} />
      <div className={classes.wrapper} style={{ flexDirection: printView ? 'column' : 'row' }}>
        {!printView && !isCourse && (
          <SideBar
            printView={printView}
            assessment={assessment}
            open={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            systems={tv}
            loading={loading}
            canShowScores={canShowScores}
            setActiveEdit={setActiveEdit}
            activeEdit={activeEdit}
            scorecardIndex={scorecardIndex}
          />
        )}
        <div id={'RightPanel'} className={classes.right}>
          {!isCourse && (
            <Filters
              {...{
                buttons: filterBtns,
                setFilter,
                selected: filter,
                loading: filterLoading,
                filter,
                changeSearch: setSearchString,
                searchString: searchString,
                activeEdit: activeEdit,

                scores: get(assessment, 'data.scores', []),
                scorecards,
                setScorecardIndex,
                scorecardIndex,
              }}
            />
          )}

          {!printView && !isCourse && (
            <>
              <TaskNotify realmId={realm_id || null} />
              <NavToTop visible={!loading && !activeEdit && !!size(tv)} />
            </>
          )}

          {userCanEdit && <ObservationCreateFab {...subscoreProps} />}

          <TaskBanner
            visible={!activeEdit && !isCourse && !printView}
            disabled={!hasScore}
            userCanEdit={userCanEdit}
            realm_id={realm_id || null}
            selectItem={selectItem}
            itemPasses={itemPasses}
            selectedItems={selectedItems}
            onCreateTasks={createTasks}
            allItems={tv}
            showSelectBoxes={showSelectBoxes}
            setShowSelectBoxes={setShowSelectBoxes}
            assessment={assessment.data}
            setSelectedItems={setSelectedItems}
          />

          <List id={'allscores'}>
            {!loading && !size(tv) && !filterLoading && !printView && <div>There are no visible scores. Try changing the filters.</div>}
            {!activeEdit && loading && <ScoreLoading />}
            {!activeEdit &&
              !loading &&
              tv.map((d, i) => {
                return (
                  <SystemScore
                    {...props}
                    key={d.id}
                    notFirst={i !== 0}
                    canShowScores={canShowScores}
                    userCanEdit={userCanEdit}
                    userCanModify={props.userCanModify}
                    assessment={assessment}
                    system={d}
                    assessmentTasks={assessmentTasks.data}
                    updateAssessmentTasks={assessmentTasks.update}
                    systemIndex={i}
                    next={get(tv, i + 1)}
                    prev={get(tv, i - 1)}
                    setCurrentAnchor={setCurrentAnchor}
                    setSidebarOpen={setSidebarOpen}
                    setActiveEdit={setActiveEdit}
                    selectItem={selectItem}
                    selectedItems={selectedItems}
                    showSelectBoxes={showSelectBoxes}
                    filter={filter}
                    printView={printView}
                    scorecard={get(scorecards, scorecardIndex)}
                    userCanLink={userCanLink}
                    isCourse={isCourse}
                    updateScores={updateScores}
                    {...subscoreProps}
                  />
                );
              })}

            {activeEdit && size(tv) > 0 && (
              <ActiveEdit
                isCourse={isCourse}
                isTemplate={isTemplate}
                activeEdit={activeEdit}
                assessment={assessment}
                assessmentTasks={assessmentTasks}
                setActiveEdit={setActiveEdit}
                setSidebarOpen={setSidebarOpen}
                updateScores={updateScores}
                setCurrentAnchor={setCurrentAnchor}
                selectedItems={selectedItems}
                treed={tv}
                filter={filter}
                scorecard={get(scorecards, scorecardIndex)}
                {...subscoreProps}
              />
            )}
          </List>
        </div>

        <Snackbar
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          open={!!alerts}
          autoHideDuration={6000}
          onClose={() => setAlerts()}
        >
          {alerts && (
            <Alert onClose={() => setAlerts()} severity={get(alerts, 'severity', 'success')}>
              {get(alerts, 'message', '')}
            </Alert>
          )}
        </Snackbar>
      </div>
    </>
  );
};

export default ReportAssessmentScoreContent;
